import { Loader } from '../Loader';
import { Sidebar } from '../Sidebar';
import { listingElasticMethods } from './elastic';
import { listingEventsMethods } from './events';
import { listingFilterAccordionMethods } from './filterAccordion';
import { listingFiltersMethods } from './filters';
import { listingRendererMethods } from './renderer';
import { listingUrlStateMethods } from './url-state';

export class Listing {
    constructor() {
        this.root = document.querySelector('[data-listing]');

        if (!this.root) {
            return;
        }

        this.title = this.root.querySelector('[data-listing-title]');
        this.description = this.root.querySelector(
            '[data-listing-description]'
        );
        this.total = this.root.querySelector('[data-listing-total]');
        this.termsSection = this.root.querySelector('[data-listing-terms]');
        this.termsTrack = this.root.querySelector('[data-listing-terms-track]');
        this.termsTrackWrapper = this.root.querySelector(
            '[data-listing-terms-track-wrapper]'
        );
        this.termsBackSlot = this.root.querySelector(
            '[data-listing-terms-back-slot]'
        );
        this.filtersSection = this.root.querySelector(
            '[data-listing-active-filters-section]'
        );
        this.filtersTrack = this.root.querySelector(
            '[data-listing-active-filters]'
        );
        this.results = this.root.querySelector('[data-listing-results]');
        this.resultsPagination = this.root.querySelector(
            '[data-listing-pagination]'
        );
        this.loader = new Loader();
        this.filtersSidebarContent = this.root.querySelector(
            '[data-listing-filters-sidebar-content]'
        );
        this.filtersSidebar = null;
        this.sortRoot = this.root.querySelector('[data-listing-sort]');
        this.sortToggle = this.root.querySelector('[data-listing-sort-toggle]');
        this.sortMenu = this.root.querySelector('[data-listing-sort-menu]');
        this.sortLabel = this.root.querySelector('[data-listing-sort-label]');
        this.sortIcon = this.root.querySelector('[data-listing-sort-icon]');
        this.bootstrap =
            typeof window !== 'undefined' && window.mdaListing
                ? window.mdaListing
                : {};
        const bootstrapSsrState = this.bootstrap.ssrState || {};
        this.ssrState = {
            page: Number(
                bootstrapSsrState.page ||
                    this.resultsPagination?.dataset.page ||
                    1
            ),
            sort: bootstrapSsrState.sort || 'newest',
            filters: this.normalizeFilters(bootstrapSsrState.filters || {}),
            priceMin: bootstrapSsrState.priceMin ?? null,
            priceMax: bootstrapSsrState.priceMax ?? null,
            termId: Number(bootstrapSsrState.termId || 0),
            termSlug: String(bootstrapSsrState.termSlug || ''),
            taxonomy: String(bootstrapSsrState.taxonomy || ''),
        };
        this.state = this.createInitialState();
        this.pendingFilters = {};
        this.pendingPriceMin = '';
        this.pendingPriceMax = '';
        this.pendingFiltersCountTimer = null;
        this.pendingSidebarApplyTimer = null;
        this.expandedFilterCollapseKeys = new Set();
        this.filterCountsLoading = false;
        this.handleInteraction = this.onRootClick.bind(this);
        this.handleDocumentClick = this.onDocumentClick.bind(this);
        this.handleWindowResize = this.onWindowResize.bind(this);

        this.bindEvents();
        this.replaceHistoryState();
        this.initFiltersSidebar();
        this.initTermsSlider();
        this.updateSortUi(this.state.sort);
        this.syncInitialStateFromUrl();
        this.filterCountsLoaded =
            !this.bootstrap.filterCountsSsr || this.hasFilterCountsInSidebar();
        this.elasticRequestLogged = false;
    }

    hasSsrResults() {
        const grid = this.results?.querySelector('[data-listing-results-grid]');

        if (!grid) {
            return false;
        }

        return Array.from(grid.children).some(element => {
            if (!element || element.nodeType !== Node.ELEMENT_NODE) {
                return false;
            }

            return !(
                element.classList.contains('listing__grid-block-spacer-wrapper') ||
                element.classList.contains('listing__grid-block') ||
                element.hasAttribute('data-listing-grid-block')
            );
        });
    }

    syncInitialStateFromUrl() {
        if (!this.canUseElastic() || !this.shouldRefetchAfterSsr()) {
            return;
        }

        if (this.hasSsrResults()) {
            return;
        }

        this.fetchAndRender({ pushUrl: null, showLoader: true });
    }

    createInitialState() {
        const price = this.getPriceFromUrl();

        return {
            taxonomy: this.root.dataset.taxonomy || '',
            termId: Number(this.root.dataset.termId || 0),
            page: Number(this.resultsPagination?.dataset.page || 1),
            sort: this.getSortFromUrl() || this.ssrState.sort,
            filters: this.getFiltersFromUrl(),
            priceMin: price.min,
            priceMax: price.max,
            phrase: this.getPhraseFromBootstrap(),
        };
    }

    isSearchMode() {
        return (
            this.bootstrap.mode === 'search' ||
            this.root?.dataset?.listingMode === 'search'
        );
    }

    isInspirationsMode() {
        return false;
    }

    getPhraseFromBootstrap() {
        return String(
            this.bootstrap.phrase || this.root?.dataset?.phrase || ''
        ).trim();
    }

    bindEvents() {
        this.root.addEventListener('click', this.handleInteraction);
        window.addEventListener('popstate', this.onPopState.bind(this));
        window.addEventListener('resize', this.handleWindowResize);
        document.addEventListener('click', this.handleDocumentClick);
    }

    replaceHistoryState() {
        window.history.replaceState(
            {
                listingState: { ...this.state },
            },
            '',
            window.location.href
        );
    }

    initFiltersSidebar() {
        if (!this.filtersSidebarContent) {
            return;
        }

        this.filtersSidebar = new Sidebar({
            overlayClassNames: ['bg-grey-900/60'],
        });

        this.filtersSidebar.content.appendChild(this.filtersSidebarContent);
        this.filtersSidebar.content.addEventListener(
            'click',
            this.handleInteraction
        );
        this.filtersSidebar.content.addEventListener(
            'input',
            this.handlePriceInput.bind(this)
        );
        this.syncPendingFiltersFromState();
        this.refreshFilterCollapseUi();
    }

    async fetchAndRender({
        pushUrl = null,
        fallbackSlug = '',
        appendResults = false,
        showLoader = true,
    } = {}) {
        if (showLoader) {
            this.root.classList.add('is-loading');
            this.loader?.show();
        }

        this.setLoadMoreLoading(true);

        try {
            const data = await this.fetchListingPayload(this.state);

            if (
                !this.isSearchMode() &&
                (!data.term || !data.term.id) &&
                pushUrl &&
                fallbackSlug
            ) {
                window.location.href = pushUrl;
                return;
            }

            if (appendResults) {
                this.renderResults(data.results || [], data.pagination || {}, {
                    append: true,
                });

                const total = Number(data.total || 0);
                const displayedCount = this.getDisplayedProductCount(total);
                const perPage = Number(
                    data.pagination?.perPage || this.bootstrap.perPage || 16
                );
                const maxPages = total > 0 ? Math.ceil(total / perPage) : 1;
                const pagination = {
                    ...(data.pagination || {}),
                    page: this.state.page,
                    perPage,
                    total,
                    maxPages,
                    current: displayedCount,
                    hasMore: displayedCount < total,
                };

                this.renderResultsPagination(pagination, { append: true });

                if (this.total) {
                    this.total.textContent = this.formatTotal(total);
                }
            } else {
                this.renderListing(data);
            }

            if (data.term?.id) {
                this.state.taxonomy = data.taxonomy || this.state.taxonomy;
                this.state.termId = Number(data.term.id);
            }

            if (pushUrl) {
                window.history.pushState(
                    { listingState: { ...this.state } },
                    '',
                    pushUrl
                );
            }
        } catch (error) {
            console.error(error);
            if (appendResults) {
                this.state.page = Math.max(1, this.state.page - 1);
            }
            if (pushUrl) {
                window.location.href = pushUrl;
            }
        } finally {
            if (showLoader) {
                this.root.classList.remove('is-loading');
                this.loader?.hide();
            }

            this.setLoadMoreLoading(false);

            // Po zmianie filtrów odśwież facet counts (ukryj niedostępne opcje, zaktualizuj liczniki).
            if (!appendResults && this.canUseElastic()) {
                this.filterCountsLoaded = false;
                void this.loadFilterTermCounts({ force: true });
            }
        }
    }
}

Object.assign(
    Listing.prototype,
    listingFilterAccordionMethods,
    listingElasticMethods,
    listingUrlStateMethods,
    listingRendererMethods,
    listingFiltersMethods,
    listingEventsMethods
);
